/**
 * Shared default-date helpers for accounting reports (#325).
 *
 * Reports are almost always run for the last *closed* month, not the partial
 * current one, so their "as of" date and default period default to the
 * previous full calendar month rather than today.
 *
 * "Which month just closed" is answered in **America/New_York** (via
 * `previousBusinessMonth`), not the viewer's local zone — see that helper for
 * why a local answer is off by a whole month around the 1st.
 *
 * Dates are anchored at 05:00Z (matching the reconciliation/statements
 * modals) so `DateInput`'s UTC getters land on the intended calendar day
 * regardless of the viewer's local timezone.
 */

import {
	businessMsToDate, DAY_MS, endOfUtcDay, etDayMs, getBusinessDateParts, previousBusinessMonth,
} from './date-utils';

/** Last calendar day of the previous month, anchored at 05:00Z. */
export const previousMonthEnd = (now: Date = new Date()): Date => {
	const { year, month } = previousBusinessMonth(now.getTime()); // month is 1-indexed
	return new Date(Date.UTC(year, month, 0, 5)); // day 0 of the next month = last day of this one
};

/** First calendar day of the previous month, anchored at 05:00Z. */
export const previousMonthStart = (now: Date = new Date()): Date => {
	const { year, month } = previousBusinessMonth(now.getTime());
	return new Date(Date.UTC(year, month - 1, 1, 5));
};

/** The previous full calendar month as a `{ start, end }` range. */
export const previousMonthRange = (now: Date = new Date()): { start: Date; end: Date } => ({
	start: previousMonthStart(now),
	end: previousMonthEnd(now),
});

/** Jan 1 of `year`, anchored at 05:00Z. */
const januaryFirst = (year: number): Date => new Date(Date.UTC(year, 0, 1, 5));

/** The last full calendar quarter before today's ET business day. */
export const previousQuarterRange = (now: Date = new Date()): { start: Date; end: Date } => {
	const today = getBusinessDateParts(etDayMs(now.getTime())); // month is 1-indexed
	const currentQuarter = Math.floor((today.month - 1) / 3);
	const year = currentQuarter === 0 ? today.year - 1 : today.year;
	const quarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
	return {
		start: new Date(Date.UTC(year, quarter * 3, 1, 5)),
		end: new Date(Date.UTC(year, quarter * 3 + 3, 0, 5)),
	};
};

/** Jan 1 – Dec 31 of the year before today's ET business year. */
export const previousYearRange = (now: Date = new Date()): { start: Date; end: Date } => {
	const year = getBusinessDateParts(etDayMs(now.getTime())).year - 1;
	return { start: januaryFirst(year), end: new Date(Date.UTC(year, 11, 31, 5)) };
};

/**
 * Jan 1 through the last closed month — the GL Report's default period, so
 * January still spans a full year rather than an empty partial month.
 *
 * The year comes from the END bound, not from "now": on Jan 1 the last closed
 * month is the previous December, and the period must span that year.
 */
export const yearThroughPreviousMonth = (now: Date = new Date()): { start: Date; end: Date } => {
	const end = previousMonthEnd(now);
	return { start: januaryFirst(end.getUTCFullYear()), end };
};

/**
 * The trailing `days` business days through today (ET), today inclusive, as
 * inclusive epoch-ms bounds: midnight UTC of `today - (days - 1)` through the last
 * ms of today's business day. For "last N days" lists filtered on business-date columns.
 */
export const trailingDaysRange = (days: number, now: Date = new Date()): { startMs: number; endMs: number } => {
	const today = etDayMs(now.getTime());
	return { startMs: today - (days - 1) * DAY_MS, endMs: endOfUtcDay(today) };
};

/** Jan 1 through today (ET business day) — the Trial Balance's default period. */
export const yearToDate = (now: Date = new Date()): { start: Date; end: Date } => {
	const end = businessMsToDate(etDayMs(now.getTime()));
	return { start: januaryFirst(end.getUTCFullYear()), end };
};
